import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, Mail, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) { setErr(res.error); return; }
    const dest = res.user.role === "customer" ? "/portal" : "/dashboard";
    nav(location.state?.from || dest, { replace: true });
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden bg-slate-900 text-white">
        <img
          src="https://images.unsplash.com/photo-1758273238594-9a9d6c20eaa2?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="cleaning"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/90 via-slate-900/60 to-transparent" />
        <div className="relative z-10 p-16 flex flex-col justify-end">
          <div className="text-xs uppercase tracking-[0.3em] text-blue-200 mb-4">DK-Elite</div>
          <h1 className="font-display font-semibold text-5xl leading-[1.05] mb-4">
            Run a cleaner business.<br/>From one calm cockpit.
          </h1>
          <p className="text-slate-200/80 max-w-md">
            Customers, jobs, schedules and invoices — without the tab-juggling.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 rounded-md bg-slate-900 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="font-display font-semibold">DK-Elite Cleaning Manager</div>
          </div>
          <h2 className="font-display text-3xl font-semibold mb-1">Welcome back</h2>
          <p className="text-sm text-slate-500 mb-8">Sign in to your account to continue.</p>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Email</label>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  data-testid="login-email"
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Password</label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  data-testid="login-password"
                  type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>
            {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-md transition-colors"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-6 text-sm text-slate-500">
            New customer? <Link to="/register" className="text-blue-600 hover:underline" data-testid="register-link">Create an account</Link>
          </div>
          <div className="mt-8 text-xs text-slate-400 border-t border-slate-100 pt-4">
            Admin demo: <code className="text-slate-600">admin@dkelite.com</code> / <code className="text-slate-600">admin123</code>
          </div>
        </div>
      </div>
    </div>
  );
}
